import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useSaveBillingsTargets } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'
import {
  cellKey,
  deriveOverall,
  inferDerived,
  numberAt,
  putCell,
  spreadAcrossMonths,
  type Grid,
  type Sheet,
} from './targets-math'
import type {
  BillingsMonth,
  BillingsTarget,
  BillingsTargetCategory,
  BillingsTargetInput,
} from '@/types'

/**
 * Set monthly targets, for the company or for one rep.
 *
 * THREE CATEGORIES, AND OVERALL IS DERIVED BUT NOT FORCED. Remediation counts toward
 * Service, per Jason's second review; the tracker still reports it separately
 * everywhere except here.
 *
 * Overall fills itself in from Software and Service and keeps up with them, which is what
 * he was doing by hand. A figure he enters himself is never overwritten, because his own
 * company figures deliberately disagree: he set Overall 10,000,000 against parts totalling
 * 11,000,000. Where they diverge the dialog says so quietly rather than correcting him.
 * Clearing an Overall leaves that month without one, and it starts following the parts
 * again the next time either part changes. See `targets-math.ts`.
 *
 * AN EMPTY BOX MEANS NO TARGET, NOT ZERO. Clearing a field sends null, which removes
 * the row. That matters for the annual figure: eleven months of targets sum to an
 * eleven-month annual target, so the twelfth does not read as a shortfall against a
 * target nobody set. "0% of target" and "no target set" are different statements.
 *
 * ACCESSIBILITY. Twelve inputs on a row cannot share one label, so every box carries
 * its own aria-label naming both month and category ("Software target, Jun 2025"). The
 * grid scrolls inside its own container rather than pushing the page sideways, the
 * save outcome goes to an aria-live region, and nothing is conveyed by colour alone.
 */

const CATEGORY_LABELS: Record<BillingsTargetCategory, string> = {
  total: 'Overall',
  software: 'Software',
  service: 'Service',
}

// Overall first: it is the number Jason opens the page for.
const CATEGORY_ORDER: BillingsTargetCategory[] = ['total', 'software', 'service']

interface TargetsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  months: BillingsMonth[]
  /** Existing rows for this scope, already filtered by the caller. */
  existing: BillingsTarget[]
  scope: 'company' | 'rep'
  /** Rep scope only. A user id when the rep is a person. */
  repUserId?: string | null
  /** Rep scope only, and only for the buckets that are not people. */
  repLabel?: string | null
  /** Shown in the title, so it is obvious whose targets these are. */
  subjectName?: string
}

export function TargetsDialog({
  open,
  onOpenChange,
  months,
  existing,
  scope,
  repUserId,
  repLabel,
  subjectName,
}: TargetsDialogProps) {
  const { toast } = useToast()
  const save = useSaveBillingsTargets()
  const [sheet, setSheet] = useState<Sheet>({ cells: {}, derived: {} })
  const grid = sheet.cells
  const [annualInput, setAnnualInput] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  const initialSheet = useMemo<Sheet>(() => {
    const cells: Grid = {}
    for (const t of existing) {
      cells[cellKey(t.category, t.month_key)] = String(t.amount)
    }
    // A stored Overall that already equals its parts is treated as derived, so saving does
    // not freeze it. One that disagrees with them was deliberate and stays put.
    return {
      cells,
      derived: inferDerived(
        cells,
        months.map((m) => m.key)
      ),
    }
  }, [existing, months])

  const initial = initialSheet.cells

  // Reopening must show what is stored, not what was typed and abandoned last time.
  useEffect(() => {
    if (open) {
      setSheet(initialSheet)
      setAnnualInput({})
      setError('')
      setStatus('')
    }
  }, [open, initialSheet])

  const setCell = (category: string, monthKey: string, value: string) =>
    setSheet((p) => deriveOverall(putCell(p, category, monthKey, value), category, monthKey))

  /**
   * Put one figure across all twelve months, so a yearly number is one action.
   *
   * THE REMAINDER GOES SOMEWHERE. An even split rounded to cents does not add back up:
   * 100,000 / 12 is 8,333.33, and twelve of those is 99,999.96. Four pence is nothing,
   * but the figure Jason typed is the figure he will look for in the annual column, and
   * a target that reads 99,999.96 when he asked for 100,000 makes him doubt the number
   * rather than the rounding. So the difference lands on the last month.
   *
   * It used to ask for the figure through window.prompt. That is not stylable, is not
   * reliably announced by a screen reader, can be suppressed by the browser outright,
   * and firing one from inside a modal loses the dialog's focus context. An inline
   * field is a few more lines and actually usable.
   */
  const spreadEvenly = (category: BillingsTargetCategory) => {
    const raw = String(annualInput[category] ?? '').replace(/[^0-9.-]/g, '')
    const annual = Number(raw)
    if (raw === '' || !Number.isFinite(annual)) {
      setError(
        `Type an annual ${CATEGORY_LABELS[category].toLowerCase()} figure before spreading it.`
      )
      return
    }
    setError('')

    const parts = spreadAcrossMonths(annual, months.length)

    setSheet((p) => {
      let next = p
      months.forEach((m, i) => {
        next = deriveOverall(putCell(next, category, m.key, String(parts[i])), category, m.key)
      })
      return next
    })
    setStatus(
      `${CATEGORY_LABELS[category]} spread across ${months.length} months, adding up to ` +
        `${annual.toLocaleString('en-CA')}. Not saved yet.`
    )
  }

  // Rounded to cents: twelve floats added up land on 20000.000000000004, and a money
  // figure should not depend on how the display happens to truncate.
  const annualOf = (category: string) =>
    Math.round(
      months.reduce((sum, m) => {
        const v = grid[cellKey(category, m.key)]
        const n = Number(v)
        return v !== undefined && v !== '' && Number.isFinite(n) ? sum + n * 100 : sum
      }, 0)
    ) / 100

  /** Months where an entered Overall differs from Software + Service. Reported, not fixed. */
  const divergentMonths = months.filter((m) => {
    const total = numberAt('total', m.key, grid)
    const software = numberAt('software', m.key, grid)
    const service = numberAt('service', m.key, grid)
    if (total === null || software === null || service === null) return false
    return Math.abs(total - (software + service)) >= 0.01
  })

  const handleSave = async () => {
    setError('')

    const payload: BillingsTargetInput[] = []
    for (const category of CATEGORY_ORDER) {
      for (const m of months) {
        const raw = grid[cellKey(category, m.key)]
        const wasSet = initial[cellKey(category, m.key)] !== undefined
        const isBlank = raw === undefined || String(raw).trim() === ''

        // Only send a blank if it is clearing something that exists. Sending blanks
        // for every untouched cell would mean 48 pointless deletes on every save.
        if (isBlank && !wasSet) continue

        if (!isBlank && !Number.isFinite(Number(raw))) {
          setError(
            `${CATEGORY_LABELS[category]} for ${m.label} is not a number. Fix it or clear the box.`
          )
          return
        }

        payload.push({
          scope,
          rep_user_id: scope === 'rep' ? (repUserId ?? null) : null,
          rep_label: scope === 'rep' && !repUserId ? (repLabel ?? null) : null,
          category,
          month_key: m.key,
          amount: isBlank ? null : Number(raw),
        })
      }
    }

    if (payload.length === 0) {
      setError('Nothing has changed, so there is nothing to save.')
      return
    }

    try {
      const result = await save.mutateAsync(payload)
      toast({
        title: 'Targets saved',
        description:
          result.cleared > 0
            ? `${result.saved} saved, ${result.cleared} cleared.`
            : `${result.saved} saved.`,
      })
      onOpenChange(false)
    } catch (err) {
      setError(explainError(err, 'Could not save those targets.'))
    }
  }

  const title =
    scope === 'company' ? 'Company targets' : `Targets for ${subjectName ?? repLabel ?? 'this rep'}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Monthly figures in CAD. Leave a box empty where there is no target: an empty box is not
            a target of zero, and the annual figure only adds up the months you set.
          </DialogDescription>
        </DialogHeader>

        <p role="status" aria-live="polite" className="sr-only">
          {status}
        </p>
        {status && <p className="text-xs text-muted-foreground">{status}</p>}

        {error && (
          <Alert variant="destructive">
            <AlertDescription role="alert">{error}</AlertDescription>
          </Alert>
        )}

        {divergentMonths.length > 0 && (
          <Alert role="note">
            <AlertDescription>
              In {divergentMonths.length} {divergentMonths.length === 1 ? 'month' : 'months'},
              Overall is not Software plus Service. That is allowed and nothing has been changed,
              but it is worth a look if it was not deliberate:{' '}
              {divergentMonths.map((m) => m.label).join(', ')}. To have Overall follow the two parts
              again, clear its box and change either part. Left cleared, that month has no Overall
              target.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          {CATEGORY_ORDER.map((category) => (
            <fieldset key={category} className="border rounded-md p-3">
              <legend className="text-sm font-medium px-1">{CATEGORY_LABELS[category]}</legend>

              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <p className="text-xs text-muted-foreground tabular-nums">
                  Year to date entered: {annualOf(category).toLocaleString('en-CA')}
                </p>
                <div className="flex items-end gap-2">
                  <div>
                    <Label htmlFor={`annual-${category}`} className="text-xs">
                      Yearly figure
                    </Label>
                    <Input
                      id={`annual-${category}`}
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      className="mt-1 w-32 tabular-nums"
                      placeholder="e.g. 100000"
                      value={annualInput[category] ?? ''}
                      onChange={(e) =>
                        setAnnualInput((p) => ({ ...p, [category]: e.target.value }))
                      }
                      aria-label={`Annual ${CATEGORY_LABELS[category]} figure to spread across the months`}
                      disabled={save.isPending}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => spreadEvenly(category)}
                    disabled={save.isPending}
                    aria-label={`Spread the annual ${CATEGORY_LABELS[category]} figure across the twelve months`}
                  >
                    Spread
                  </Button>
                </div>
              </div>

              {/* Own scroll container: twelve number boxes are wider than a phone and
                  the page body must never scroll sideways. */}
              <div className="overflow-x-auto">
                <div className="flex gap-2 min-w-max pb-1">
                  {months.map((m) => {
                    const id = `target-${category}-${m.key}`
                    return (
                      <div key={m.key} className="w-24 shrink-0">
                        <Label htmlFor={id} className="text-xs block truncate">
                          {m.label}
                        </Label>
                        <Input
                          id={id}
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          className="mt-1 tabular-nums"
                          placeholder="none"
                          value={grid[cellKey(category, m.key)] ?? ''}
                          onChange={(e) => setCell(category, m.key, e.target.value)}
                          // The visible <Label> is the month alone, which repeats four
                          // times down the dialog. This is what a screen reader
                          // announces, so it has to carry the category as well.
                          aria-label={`${CATEGORY_LABELS[category]} target, ${m.label}`}
                          disabled={save.isPending}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            </fieldset>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={save.isPending}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save targets'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
